import React, { useEffect, useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useData } from '../../contexts/DataContext';
import { useRoleAccess } from '../../hooks/useRoleAccess';
import { useNavigate } from 'react-router-dom';
import {
  Users,
  BookOpen,
  Calendar,
  CheckCircle,
  Bell,
  FileText,
  UserPlus,
  Settings,
  Clipboard,
  RefreshCw,
  Sparkles
} from 'lucide-react';
import Loader from '../../ui/Loader';
import WhatsAppAnalytics from '../../components/WhatsAppAnalytics';

const Dashboard = () => {
  const { user, isAdmin, isTeacher } = useAuth();
  const { loadDashboardStats, loading, organization } = useData();
  const { hasPermission } = useRoleAccess();
  const navigate = useNavigate();
  
  // Get brand colors from organization
  const brandColors = useMemo(() => {
    const colors = organization?.brandColors || organization?.branding?.brandColors;
    return {
      primary: colors?.primary || '#4f46e5',
      secondary: colors?.secondary || '#7c3aed'
    };
  }, [organization]);
  
  const isLoading = loading.dashboard;

  useEffect(() => {
    loadDashboardStats();
  }, []);

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good Morning';
    if (hour < 17) return 'Good Afternoon';
    return 'Good Evening';
  };

  const currentDate = new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });

  return (
    <div className="space-y-8 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 animate-in fade-in duration-500">
      {/* Welcome Section */}
      <div 
        className="relative overflow-hidden rounded-3xl p-6 text-white shadow-xl transition-all hover:shadow-2xl"
        style={{
          background: `linear-gradient(135deg, var(--primary-color, #4f46e5) 0%, var(--secondary-color, #7c3aed) 100%)`
        }}
      >
        <div className="relative z-10 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <div className="flex items-center gap-2 mb-1 opacity-90">
               <Calendar className="h-4 w-4" />
               <span className="text-xs font-bold uppercase tracking-widest">{currentDate}</span>
            </div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl md:text-3xl font-black tracking-tight">
                {getGreeting()}
              </h1>
              <Sparkles className="h-6 w-6 text-yellow-300 animate-pulse hidden md:block" />
            </div>

          </div>
          <button 
            onClick={() => loadDashboardStats()} 
            className="p-3 bg-white/10 hover:bg-white/20 rounded-xl transition-all backdrop-blur-sm border border-white/10 group"
            title="Refresh Dashboard"
          >
            <RefreshCw className="h-5 w-5 text-white group-hover:rotate-180 transition-transform duration-700" />
          </button>
        </div>
        
        {/* Decorative elements */}
        <div className="absolute top-0 right-0 -mr-16 -mt-16 w-64 h-64 bg-white/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 -ml-16 -mb-16 w-48 h-48 bg-black/10 rounded-full blur-3xl"></div>
      </div>

      {isLoading ? (
        <div className="flex flex-col items-center justify-center py-20 bg-white rounded-3xl shadow-sm border border-gray-100">
          <Loader />
          <p className="mt-4 text-gray-400 text-xs font-bold uppercase tracking-widest">Synchronizing Dashboard...</p>
        </div>
      ) : (
        <>
          {/* WhatsApp Analytics - Only for Admin */}
          {isAdmin && (
            <div className="animate-in slide-in-from-bottom-4 duration-700 delay-100">
              <WhatsAppAnalytics brandColors={brandColors} />
            </div>
          )}

          {/* Quick Actions Section */}
          <div className="space-y-4 animate-in slide-in-from-bottom-4 duration-700 delay-200">
            <div className="flex items-center gap-3 px-1">
              <div 
                className="h-8 w-1 rounded-full bg-gradient-to-b from-[var(--primary-color)] to-[var(--secondary-color)]" 
              ></div>
              <h3 className="text-lg font-bold text-gray-900">Quick Actions</h3>
            </div>
            
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
              {hasPermission('canViewAllStudents') && (
                <ActionCard 
                  icon={Users} 
                  label={isAdmin ? 'Students' : 'My Students'} 
                  onClick={() => navigate('/students')} 
                  color="blue"
                  desc="Manage Enrollments"
                />
              )}

              {hasPermission('canMarkAttendance') && (
                <ActionCard 
                  icon={CheckCircle} 
                  label="Attendance" 
                  onClick={() => navigate('/attendance')} 
                  color="emerald"
                  desc="Track Daily Log"
                />
              )}

              {hasPermission('canViewAllResults') && (
                <ActionCard 
                  icon={FileText} 
                  label="Results" 
                  onClick={() => navigate('/exam-results')} 
                  color="amber"
                  desc="Publish Marks"
                />
              )}

              {hasPermission('canViewAllCalendar') && (
                <ActionCard 
                  icon={Calendar} 
                  label="Calendar" 
                  onClick={() => navigate('/calendar')} 
                  color="purple"
                  desc="Events & Holidays"
                />
              )}

              {hasPermission('canViewAllNotices') && (
                <ActionCard 
                  icon={Bell} 
                  label="Notices" 
                  onClick={() => navigate('/notices')} 
                  color="rose"
                  desc="Broadcast Updates"
                />
              )}

              {isAdmin && (
                <>
                  <ActionCard 
                    icon={UserPlus} 
                    label="Users" 
                    onClick={() => navigate('/users')} 
                    color="indigo"
                    desc="Manage Staff"
                  />
                  <ActionCard 
                    icon={BookOpen} 
                    label="Classes" 
                    onClick={() => navigate('/classes')} 
                    color="cyan"
                    desc="Academic Setup"
                  />
                  <ActionCard 
                    icon={Settings} 
                    label="Settings" 
                    onClick={() => navigate('/settings')} 
                    color="slate"
                    desc="System Config"
                  />
                </>
              )}

              {isTeacher && (
                <ActionCard 
                  icon={Clipboard} 
                  label="Test Results" 
                  onClick={() => navigate('/test-results')} 
                  color="orange"
                  desc="Class Tests"
                />
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

// Premium Action Card Component
const ActionCard = ({ icon: Icon, label, onClick, color = 'indigo', desc }) => {
  const colorStyles = {
    blue: 'bg-blue-50 text-blue-600 group-hover:bg-blue-600 group-hover:text-white',
    emerald: 'bg-emerald-50 text-emerald-600 group-hover:bg-emerald-600 group-hover:text-white',
    amber: 'bg-amber-50 text-amber-600 group-hover:bg-amber-600 group-hover:text-white',
    purple: 'bg-purple-50 text-purple-600 group-hover:bg-purple-600 group-hover:text-white',
    rose: 'bg-rose-50 text-rose-600 group-hover:bg-rose-600 group-hover:text-white',
    indigo: 'bg-indigo-50 text-indigo-600 group-hover:bg-indigo-600 group-hover:text-white',
    cyan: 'bg-cyan-50 text-cyan-600 group-hover:bg-cyan-600 group-hover:text-white',
    slate: 'bg-slate-50 text-slate-600 group-hover:bg-slate-600 group-hover:text-white',
    orange: 'bg-orange-50 text-orange-600 group-hover:bg-orange-600 group-hover:text-white',
  };

  const ringStyles = {
    blue: 'group-hover:ring-blue-100',
    emerald: 'group-hover:ring-emerald-100',
    amber: 'group-hover:ring-amber-100',
    purple: 'group-hover:ring-purple-100',
    rose: 'group-hover:ring-rose-100',
    indigo: 'group-hover:ring-indigo-100',
    cyan: 'group-hover:ring-cyan-100',
    slate: 'group-hover:ring-slate-100',
    orange: 'group-hover:ring-orange-100',
  };

  return (
    <button
      onClick={onClick}
      className={`group flex flex-col items-start justify-between p-5 bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 w-full h-full text-left relative overflow-hidden`}
    >
      <div className={`p-3 rounded-xl transition-all duration-300 mb-3 ${colorStyles[color]}`}>
        <Icon className="h-6 w-6" strokeWidth={2.5} />
      </div>
      
      <div>
        <span className="block text-sm font-bold text-gray-900 group-hover:text-[var(--primary-color)] transition-colors">
          {label}
        </span>
        {desc && (
          <span className="text-[10px] font-medium text-gray-400 group-hover:text-gray-500 transition-colors mt-0.5 block">
            {desc}
          </span>
        )}
      </div>

      <div className={`absolute inset-0 rounded-2xl ring-2 ring-transparent transition-all duration-300 pointer-events-none ${ringStyles[color]}`} />
    </button>
  );
};

export default Dashboard;
